'use strict';

/**
 * Conditional-progression rule — "proceed, or reveal the skip options?"
 *
 * Some questions carry hidden options (a "prefer not to say", say) that only
 * surface when a respondent tries to skip past. When the respondent clicks next,
 * the widget must decide: let them through, or hold them on the page, reveal the
 * hidden options, and show the required message.
 *
 * That decision was written three times — in `Base`, `Grid`, and `Dropdown` —
 * each measuring the rendered page directly through the shared `isPassed`
 * predicate. The predicate was one function, but the measure/decide/apply loop
 * around it was not, and nothing verified the three agreed. This is the data-only
 * port of that predicate: a pure function of the question object and a few facts
 * the caller measures. The widget still measures the screen and applies the
 * outcome (revealing elements, showing messages); only the decision lives here.
 *
 * Ported verbatim from `Base/index.js` `isPassed`. The three families differ only
 * in how they measure `answered` and `hidden` off their own markup — a checkbox
 * by `:checked`, a text grid cell by a non-empty value, a dropdown option by
 * `:selected` — never in the decision itself, so no divergence option is needed.
 * See `./README.md` for the contract and `../answers-adapter` for the snapshot.
 */

import { answerVariablesOf } from '../answers-adapter';

/**
 * Is a stored value a selection? Presence, not identity: neither `undefined`
 * (unwritten) nor `''` (written while empty). Mirrors `validation.js`
 * `isSelected`; see the README's "Reading an answer".
 */
function isAnswered(answers, name) {
  var value = answers[name];
  return value !== undefined && value !== '';
}

/**
 * Has the respondent answered any variable this question owns? Counts over
 * `answerVariablesOf` (the declared variables minus `passthru`) so an untouched
 * page with server-set passthru values does not read as answered. A caller that
 * still measures selections off the DOM injects `uiState.answeredCount` instead,
 * matching how `helpers.js` injects counts for Matrix and the Grid displaymax
 * helper.
 */
function hasAnswer(question, answers, uiState) {
  if (typeof uiState.answeredCount === 'number') {
    return uiState.answeredCount > 0;
  }
  return answerVariablesOf(question).some(function (name) {
    return isAnswered(answers, name);
  });
}

/**
 * May the respondent proceed, or must the widget reveal the skip options?
 *
 * The port of `Base/index.js` `isPassed`. Proceed when any of these hold:
 *
 *   - the question declares no `show_on_skip` options (nothing to reveal);
 *   - the respondent has answered something;
 *   - the question is `SOFT` required (treated as answered-enough here — the
 *     soft prompt is handled elsewhere); or
 *   - no skip option is still hidden and the question is not `HARD` required.
 *
 * Otherwise hold the respondent on the page and report which option codes to
 * reveal. The list is the codes the caller measured as still hidden; the caller
 * decides how to reveal them, since each family locates its options differently.
 *
 * @param {Object} question The question object, read-only. Supplies
 *     `show_on_skip` and `required`.
 * @param {Object} answers Snapshot keyed by variable name, read-only. Used only
 *     when `uiState.answeredCount` is not injected.
 * @param {Object} [uiState] Screen facts the question object cannot supply:
 *     `hiddenOptionCodes` (skip-option codes still hidden right now) and the
 *     optional `answeredCount` (selections the caller measured off the DOM).
 * @returns {{ proceed: boolean, reveal: Array }} `proceed` is the decision;
 *     `reveal` is the option codes to reveal when not proceeding, else empty.
 */
export function shouldProceed(question, answers, uiState) {
  question = question || {};
  answers = answers || {};
  uiState = uiState || {};

  var elms = question.show_on_skip || [];
  var hiddenOptionCodes = Array.isArray(uiState.hiddenOptionCodes)
    ? uiState.hiddenOptionCodes
    : [];

  var proceed;
  if (
    !question.show_on_skip ||
    elms.length === 0 ||
    hasAnswer(question, answers, uiState) ||
    question.required === 'SOFT'
  ) {
    proceed = true;
  } else if (hiddenOptionCodes.length === 0 && question.required !== 'HARD') {
    proceed = true;
  } else {
    proceed = false;
  }

  return {
    proceed: proceed,
    reveal: proceed ? [] : hiddenOptionCodes.slice(),
  };
}
